import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, History } from "lucide-react";
import { api } from "../../../lib/api";
import { rowItem, usableRows } from "./reminderMessage";

/** First reminder / Follow-up / Final request / Continuation badge for a logged reminder. */
export function reminderKind(log) {
    const n = log.template_name || "";
    if (/final/i.test(n)) return { label: "Final request", cls: "bg-rose-50 text-rose-700" };
    if (log.template_category === "continuation" || /continu|renewal|resum|discontinu|scheduling/i.test(n)) return { label: "Continuation", cls: "bg-indigo-50 text-indigo-700" };
    if (/follow-up|repeated|no response|long-pending/i.test(n)) return { label: "Follow-up", cls: "bg-amber-50 text-amber-800" };
    if (log.template_category === "special") return { label: "Update", cls: "bg-emerald-50 text-emerald-700" };
    return { label: "Reminder", cls: "bg-slate-100 text-slate-700" };
}

export default function RecentReminders({ parent, refreshKey }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        if (!parent?.id) return setItems([]);
        let alive = true;
        api.get(`/admin/fee-reminders/parents/${parent.id}/log`, { params: { limit: 10 } })
            .then(({ data }) => alive && setItems(data.items || [])).catch(() => alive && setItems([]));
        return () => { alive = false; };
    }, [parent?.id, refreshKey]);
    if (!parent) return null;
    const copy = async (m) => {
        try { await navigator.clipboard.writeText(m); toast.success("Previous message copied"); } catch { toast.error("Couldn't copy — please select and copy manually"); }
    };
    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3" data-testid="fee-recent-reminders">
            <h3 className="font-heading font-bold text-[#0F172A] inline-flex items-center gap-2"><History className="w-4 h-4" /> Recent reminders</h3>
            {items.length === 0 && <p className="text-sm text-slate-500" data-testid="fee-recent-empty">No reminders sent to this parent yet.</p>}
            <ul className="space-y-2">
                {items.map((l, i) => {
                    const kind = reminderKind(l);
                    const rows = usableRows(l.details || []);
                    return (
                        <li key={l.id} className="border border-slate-200 rounded-xl p-3" data-testid={`fee-recent-${i}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${kind.cls}`}>{kind.label}</span>
                                        <span className="font-semibold text-sm text-[#0F172A]">{l.template_name || "Reminder"}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {new Date(l.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · Opened in WhatsApp
                                        {l.teaching_mode ? ` · ${l.teaching_mode === "home" ? "Home Tuition" : "1:1 Live Online"}` : ""}
                                        {l.next_follow_up ? ` · Follow up ${l.next_follow_up}` : ""}
                                    </div>
                                    {rows.length > 0 && <div className="text-xs text-slate-600 mt-1">{rows.map(rowItem).join(" · ")}</div>}
                                </div>
                                <button type="button" onClick={() => copy(l.message)} aria-label="Copy previous message" data-testid={`fee-recent-copy-${i}`}
                                    className="w-9 h-9 shrink-0 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600"><Copy className="w-4 h-4" /></button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
